import { oc } from "@orpc/contract";
import { PRINT_PAGE_SIZE } from "@packages/core/contract/print.ts";
import { z } from "zod";
import { CycleAudienceSchema } from "./cycleAudienceSchemas.ts";
import { CycleCalibrationEvidenceSchema, CycleCalibrationNoteSchema, CycleCalibrationRevieweeSchema, CycleCalibrationSummarySchema } from "./cycleCalibrationSchemas.ts";
import { CycleQuestionResultsSchema } from "./cycleQuestionSchemas.ts";
import { CycleRevieweeScoreSchema, CycleScoreSummarySchema } from "./cycleScoresSchemas.ts";
import { NineBoxCellIdSchema, NineBoxResultSchema } from "./nineBox.ts";
import { MemberFiltersSchema } from "./reviewee-filters/memberFilters.ts";
import { CycleRevieweeExceptions, RosterFiltersSchema, ScopeRosterFiltersSchema } from "./reviewee-filters/rosterFilters.ts";
import {
    CycleActionAvailabilitySchema,
    CyclePersonSchema,
    CycleReminderInformationSchema,
    CycleReminderTypes,
    CycleRevieweeSchema,
    CycleRosterSortColumns,
    CycleScopedProgressSchema,
    CycleStates,
    REMINDER_MESSAGE_MAX_LENGTH,
    ReviewCycleActionSchema,
    ReviewCycleDetailSchema,
    ReviewCycleSummarySchema
} from "./reviewCycleSchemas.ts";
import { CALIBRATION_NOTE_MAX_LENGTH } from "./reviewConstraints.ts";
import { ReviewThemesWithCountsSchema } from "./reviewThemeCounts.ts";
import { CycleReviewTypes } from "./reviewTypes.ts";
import { MAX_SUPPORTED_SCALE } from "./scoreBands.ts";
import { DEFAULT_SCORE_BUCKET_GRANULARITY, ScoreBucketGranularitySchema } from "./scoreBuckets.ts";

const ListReviewCyclesInputSchema = z.object({
    orgId: z.string(),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(25),
    q: z.string().optional(),
    participantIds: z.array(z.string()).optional(),
    reviewTypes: z.array(z.enum(CycleReviewTypes)).optional(),
    creatorIds: z.array(z.string()).optional(),
    states: z.array(z.enum(CycleStates)).optional(),
    evaluationYear: z.number().int().optional()
});

const ListReviewCyclesOutputSchema = z.object({
    items: z.array(ReviewCycleSummarySchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

export type ListReviewCyclesInput = z.infer<typeof ListReviewCyclesInputSchema>;
export type ListReviewCyclesOutput = z.infer<typeof ListReviewCyclesOutputSchema>;

const ListCycleFilterOptionsInputSchema = z.object({
    orgId: z.string()
});

const ListCycleFilterOptionsOutputSchema = z.object({
    creators: z.array(CyclePersonSchema),
    evaluationYears: z.array(z.number().int())
});

const SearchCycleRevieweesInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    selectedIds: z.array(z.string()).optional()
});

const SearchCycleRevieweesOutputSchema = z.object({
    items: z.array(CyclePersonSchema)
});

const GetReviewCycleInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string()
});

const GetCycleProgressInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    ...MemberFiltersSchema.shape
});

const CycleActionInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string()
});

const DuplicateReviewCycleOutputSchema = z.object({
    id: z.string()
});

const CreateReviewCycleInputSchema = z.object({
    orgId: z.string()
});

const CreateReviewCycleOutputSchema = z.object({
    id: z.string()
});

const PatchReviewCycleInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    actions: z.array(ReviewCycleActionSchema)
});

const PatchReviewCycleOutputSchema = z.object({
    references: z.record(z.string(), z.string())
});

export const ListCycleRevieweesInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(25),
    sortBy: z.enum(CycleRosterSortColumns).default("name"),
    sortDir: z.enum(["asc", "desc"]).default("asc"),
    ...MemberFiltersSchema.shape,
    ...RosterFiltersSchema.shape
});

const ListCycleRevieweesOutputSchema = z.object({
    items: z.array(CycleRevieweeSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

export type ListCycleRevieweesInput = z.infer<typeof ListCycleRevieweesInputSchema>;
export type ListCycleRevieweesOutput = z.infer<typeof ListCycleRevieweesOutputSchema>;

const GetCycleExceptionCountsInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    ...MemberFiltersSchema.shape,
    ...RosterFiltersSchema.omit({ exceptions: true }).shape
});

export type GetCycleExceptionCountsInput = z.infer<typeof GetCycleExceptionCountsInputSchema>;

const GetCycleExceptionCountsOutputSchema = z.object({
    counts: z.array(z.object({ exception: z.enum(CycleRevieweeExceptions), count: z.number().int() }))
});

const GetCycleScoreSummaryInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    granularity: ScoreBucketGranularitySchema.default(DEFAULT_SCORE_BUCKET_GRANULARITY),
    ...MemberFiltersSchema.shape
});

export const ListCycleRevieweeScoresInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    continuationToken: z.string().optional(),
    pageSize: z.number().int().positive().max(PRINT_PAGE_SIZE).default(25),
    searchTerm: z.string().optional(),
    sortDir: z.enum(["asc", "desc"]).default("desc"),
    band: z.number().optional(),
    granularity: ScoreBucketGranularitySchema.default(DEFAULT_SCORE_BUCKET_GRANULARITY),
    maxScale: z.number().int().min(1).max(MAX_SUPPORTED_SCALE).optional(),
    ...MemberFiltersSchema.shape
});

const ListCycleRevieweeScoresOutputSchema = z.object({
    items: z.array(CycleRevieweeScoreSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

const GetCycleReminderInformationInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    ...MemberFiltersSchema.shape
});

const ListCycleReminderRecipientsInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    memberIds: z.array(z.string()).min(1)
});

const ListCycleReminderRecipientsOutputSchema = z.object({
    items: z.array(CyclePersonSchema)
});

const SendCycleRemindersInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    reminderTypes: z.array(z.enum(CycleReminderTypes)).min(1),
    message: z.string().max(REMINDER_MESSAGE_MAX_LENGTH).optional(),
    filters: MemberFiltersSchema
});

const RosterActionTargetSchema = z.discriminatedUnion("mode", [z.object({ mode: z.literal("members"), memberIds: z.array(z.string()).min(1) }), z.object({ mode: z.literal("scope"), filters: MemberFiltersSchema.extend(ScopeRosterFiltersSchema.shape) })]);

const RosterActionInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    target: RosterActionTargetSchema
});

export type RosterActionTarget = z.infer<typeof RosterActionTargetSchema>;

const CycleRevieweeInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    revieweeId: z.string()
});

const CycleTemporaryManagerSchema = z.object({ manager: CyclePersonSchema.optional() });

const SetCycleTemporaryManagerInputSchema = CycleRevieweeInputSchema.extend({ managerId: z.string().nullable() });

const CycleReviewerListSchema = z.object({
    reviewers: z.array(
        CyclePersonSchema.extend({
            requestCount: z.number().optional()
        })
    )
});

const SearchCyclePeerReviewCandidatesInputSchema = CycleRevieweeInputSchema.extend({
    q: z.string().optional(),
    excludedIds: z.array(z.string()).optional()
});

const CyclePeerReviewCandidateSchema = CyclePersonSchema.extend({
    requestCount: z.number().optional()
});

export type CyclePeerReviewCandidate = z.infer<typeof CyclePeerReviewCandidateSchema>;

const SearchCyclePeerReviewCandidatesOutputSchema = z.object({ items: z.array(CyclePeerReviewCandidateSchema) });

const SetCycleReviewersInputSchema = CycleRevieweeInputSchema.extend({ reviewerIds: z.array(z.string()) });

const CycleUpwardReviewerGroupsSchema = z.object({
    directReports: z.array(CyclePersonSchema),
    extendedLine: z.array(CyclePersonSchema)
});

const SearchCycleUpwardReviewCandidatesInputSchema = CycleRevieweeInputSchema.extend({
    q: z.string().optional(),
    excludedIds: z.array(z.string()).optional(),
    continuationToken: z.string().optional()
});

const SearchCycleUpwardReviewCandidatesOutputSchema = z.object({
    items: z.array(CyclePersonSchema),
    continuationToken: z.string().optional()
});

const SearchCycleRevieweeCandidatesInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    q: z.string().optional(),
    excludedIds: z.array(z.string()).optional(),
    continuationToken: z.string().optional()
});

const SearchCycleRevieweeCandidatesOutputSchema = z.object({
    items: z.array(CyclePersonSchema),
    continuationToken: z.string().optional()
});

const SearchCycleAudiencePreviewInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    q: z.string().optional(),
    continuationToken: z.string().optional()
});

const CycleAudiencePreviewItemSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    imageUrl: z.string().optional(),
    hireDate: z.string().nullable(),
    entryDate: z.string().nullable()
});

export type CycleAudiencePreviewItem = z.infer<typeof CycleAudiencePreviewItemSchema>;

const SearchCycleAudiencePreviewOutputSchema = z.object({
    items: z.array(CycleAudiencePreviewItemSchema),
    totalCount: z.number(),
    continuationToken: z.string().optional()
});

export type SearchCycleAudiencePreviewOutput = z.infer<typeof SearchCycleAudiencePreviewOutputSchema>;

const AddCycleRevieweesInputSchema = z
    .object({
        orgId: z.string(),
        cycleId: z.string(),
        memberIds: z.array(z.string()).default([]),
        segmentIds: z.array(z.string()).default([])
    })
    .refine(input => input.memberIds.length > 0 || input.segmentIds.length > 0, { message: "Pick at least one member or segment." });

const AddCycleRevieweesOutcomeBucketSchema = z.object({
    count: z.number(),
    people: z.array(CyclePersonSchema)
});

const AddCycleRevieweesOutcomeSchema = z.object({
    added: AddCycleRevieweesOutcomeBucketSchema,
    addedUpcoming: AddCycleRevieweesOutcomeBucketSchema,
    alreadyAdded: AddCycleRevieweesOutcomeBucketSchema,
    missingHireDate: AddCycleRevieweesOutcomeBucketSchema,
    unplaceable: AddCycleRevieweesOutcomeBucketSchema,
    nextEntryDate: z.string().optional()
});

export type AddCycleRevieweesOutcome = z.infer<typeof AddCycleRevieweesOutcomeSchema>;

const GetCycleActionAvailabilityInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    ...MemberFiltersSchema.shape,
    ...ScopeRosterFiltersSchema.shape,
    memberIds: z.array(z.string()).min(1).optional()
});

export type GetCycleActionAvailabilityInput = z.infer<typeof GetCycleActionAvailabilityInputSchema>;

export const GetCycleReviewThemesInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    ...MemberFiltersSchema.shape
});

export const GetCycleQuestionResultsInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string(),
    reviewTypes: z.array(z.enum(CycleReviewTypes)).optional(),
    ...MemberFiltersSchema.shape
});

export type GetCycleQuestionResultsInput = z.infer<typeof GetCycleQuestionResultsInputSchema>;
export type GetCycleScoreSummaryInput = z.infer<typeof GetCycleScoreSummaryInputSchema>;
export type ListCycleRevieweeScoresInput = z.infer<typeof ListCycleRevieweeScoresInputSchema>;
export type ListCycleRevieweeScoresOutput = z.infer<typeof ListCycleRevieweeScoresOutputSchema>;
export type GetCycleReviewThemesInput = z.infer<typeof GetCycleReviewThemesInputSchema>;

const CycleScopeInputSchema = z.object({
    orgId: z.string(),
    cycleId: z.string()
});

const PlacementScalesSchema = z.object({
    performanceMaxScale: z.number().int().min(1).max(MAX_SUPPORTED_SCALE).optional(),
    potentialMaxScale: z.number().int().min(1).max(MAX_SUPPORTED_SCALE).optional()
});

const GetCycleCalibrationSummaryInputSchema = z.object({ ...CycleScopeInputSchema.shape, ...MemberFiltersSchema.shape });

const GetCycleNineBoxInputSchema = z.object({ ...CycleScopeInputSchema.shape, ...PlacementScalesSchema.shape, ...MemberFiltersSchema.shape });

export const ListCycleCalibrationRevieweesInputSchema = z.object({
    ...CycleScopeInputSchema.shape,
    ...PlacementScalesSchema.shape,
    continuationToken: z.string().optional(),
    pageSize: z.number().int().positive().max(PRINT_PAGE_SIZE).default(25),
    searchTerm: z.string().optional(),
    cellId: NineBoxCellIdSchema.optional(),
    ...MemberFiltersSchema.shape
});

const ListCycleCalibrationRevieweesOutputSchema = z.object({
    items: z.array(CycleCalibrationRevieweeSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

const GetCycleCalibrationEvidenceInputSchema = z.object({
    ...CycleScopeInputSchema.shape,
    revieweeId: z.string(),
    overallScore: z.number().optional()
});

const CycleCalibrationNoteInputSchema = z.object({ ...CycleScopeInputSchema.shape, revieweeId: z.string() });

const SaveCycleCalibrationNoteInputSchema = z.object({ ...CycleCalibrationNoteInputSchema.shape, content: z.string().max(CALIBRATION_NOTE_MAX_LENGTH) });

export type GetCycleCalibrationSummaryInput = z.infer<typeof GetCycleCalibrationSummaryInputSchema>;
export type GetCycleNineBoxInput = z.infer<typeof GetCycleNineBoxInputSchema>;
export type ListCycleCalibrationRevieweesInput = z.infer<typeof ListCycleCalibrationRevieweesInputSchema>;
export type ListCycleCalibrationRevieweesOutput = z.infer<typeof ListCycleCalibrationRevieweesOutputSchema>;
export type GetCycleCalibrationEvidenceInput = z.infer<typeof GetCycleCalibrationEvidenceInputSchema>;
export type CycleCalibrationNoteInput = z.infer<typeof CycleCalibrationNoteInputSchema>;
export type SaveCycleCalibrationNoteInput = z.infer<typeof SaveCycleCalibrationNoteInputSchema>;

export const reviewCyclesContract = {
    listReviewCycles: oc.route({ method: "GET", path: "/list-review-cycles" }).errors({ UPSTREAM_FAILURE: {} }).input(ListReviewCyclesInputSchema).output(ListReviewCyclesOutputSchema),
    listCycleFilterOptions: oc.route({ method: "GET", path: "/list-filter-options" }).errors({ UPSTREAM_FAILURE: {} }).input(ListCycleFilterOptionsInputSchema).output(ListCycleFilterOptionsOutputSchema),
    searchCycleReviewees: oc.route({ method: "GET", path: "/search-cycle-reviewees" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchCycleRevieweesInputSchema).output(SearchCycleRevieweesOutputSchema),
    getReviewCycle: oc.route({ method: "GET", path: "/get-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(GetReviewCycleInputSchema).output(ReviewCycleDetailSchema),
    getCycleProgress: oc.route({ method: "GET", path: "/get-cycle-progress" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(GetCycleProgressInputSchema).output(CycleScopedProgressSchema),
    listCycleReviewees: oc.route({ method: "GET", path: "/list-cycle-reviewees" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(ListCycleRevieweesInputSchema).output(ListCycleRevieweesOutputSchema),
    getCycleExceptionCounts: oc.route({ method: "GET", path: "/get-cycle-exception-counts" }).errors({ UPSTREAM_FAILURE: {} }).input(GetCycleExceptionCountsInputSchema).output(GetCycleExceptionCountsOutputSchema),
    getCycleScoreSummary: oc.route({ method: "GET", path: "/get-cycle-score-summary" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(GetCycleScoreSummaryInputSchema).output(CycleScoreSummarySchema),
    listCycleRevieweeScores: oc.route({ method: "GET", path: "/list-cycle-reviewee-scores" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(ListCycleRevieweeScoresInputSchema).output(ListCycleRevieweeScoresOutputSchema),
    getCycleReminderInformation: oc.route({ method: "GET", path: "/get-cycle-reminder-information" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(GetCycleReminderInformationInputSchema).output(CycleReminderInformationSchema),
    listCycleReminderRecipients: oc.route({ method: "GET", path: "/list-cycle-reminder-recipients" }).errors({ UPSTREAM_FAILURE: {} }).input(ListCycleReminderRecipientsInputSchema).output(ListCycleReminderRecipientsOutputSchema),
    sendCycleReminders: oc.route({ method: "POST", path: "/send-cycle-reminders" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(SendCycleRemindersInputSchema).output(z.void()),
    getCycleActionAvailability: oc.route({ method: "GET", path: "/get-cycle-action-availability" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(GetCycleActionAvailabilityInputSchema).output(CycleActionAvailabilitySchema),
    getCycleTemporaryManager: oc.route({ method: "GET", path: "/get-cycle-temporary-manager" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleRevieweeInputSchema).output(CycleTemporaryManagerSchema),
    setCycleTemporaryManager: oc.route({ method: "POST", path: "/set-cycle-temporary-manager" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(SetCycleTemporaryManagerInputSchema).output(z.void()),
    listCyclePeerReviewers: oc.route({ method: "GET", path: "/list-cycle-peer-reviewers" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleRevieweeInputSchema).output(CycleReviewerListSchema),
    searchCyclePeerReviewCandidates: oc.route({ method: "GET", path: "/search-cycle-peer-review-candidates" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchCyclePeerReviewCandidatesInputSchema).output(SearchCyclePeerReviewCandidatesOutputSchema),
    setCyclePeerReviewers: oc.route({ method: "POST", path: "/set-cycle-peer-reviewers" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(SetCycleReviewersInputSchema).output(z.void()),
    listCycleUpwardReviewers: oc.route({ method: "GET", path: "/list-cycle-upward-reviewers" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleRevieweeInputSchema).output(CycleReviewerListSchema),
    getCycleUpwardReviewerGroups: oc.route({ method: "GET", path: "/get-cycle-upward-reviewer-groups" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleRevieweeInputSchema).output(CycleUpwardReviewerGroupsSchema),
    searchCycleUpwardReviewCandidates: oc.route({ method: "GET", path: "/search-cycle-upward-review-candidates" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchCycleUpwardReviewCandidatesInputSchema).output(SearchCycleUpwardReviewCandidatesOutputSchema),
    setCycleUpwardReviewers: oc.route({ method: "POST", path: "/set-cycle-upward-reviewers" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(SetCycleReviewersInputSchema).output(z.void()),
    removeCycleReviewees: oc.route({ method: "POST", path: "/remove-cycle-reviewees" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(RosterActionInputSchema).output(z.void()),
    searchCycleRevieweeCandidates: oc.route({ method: "GET", path: "/search-cycle-reviewee-candidates" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchCycleRevieweeCandidatesInputSchema).output(SearchCycleRevieweeCandidatesOutputSchema),
    addCycleReviewees: oc.route({ method: "POST", path: "/add-cycle-reviewees" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(AddCycleRevieweesInputSchema).output(AddCycleRevieweesOutcomeSchema),
    getCycleAudience: oc.route({ method: "GET", path: "/get-cycle-audience" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleScopeInputSchema).output(CycleAudienceSchema),
    searchCycleAudiencePreview: oc.route({ method: "GET", path: "/search-cycle-audience-preview" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(SearchCycleAudiencePreviewInputSchema).output(SearchCycleAudiencePreviewOutputSchema),
    shareCycleReviews: oc.route({ method: "POST", path: "/share-cycle-reviews" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(RosterActionInputSchema).output(z.void()),
    unshareCycleReviews: oc.route({ method: "POST", path: "/unshare-cycle-reviews" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {}, FORBIDDEN: {} }).input(RosterActionInputSchema).output(z.void()),
    getCycleReviewThemes: oc.route({ method: "GET", path: "/get-cycle-review-themes" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(GetCycleReviewThemesInputSchema).output(ReviewThemesWithCountsSchema),

    getCycleCalibrationSummary: oc.route({ method: "GET", path: "/get-cycle-calibration-summary" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(GetCycleCalibrationSummaryInputSchema).output(CycleCalibrationSummarySchema),
    getCycleNineBox: oc.route({ method: "GET", path: "/get-cycle-nine-box" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(GetCycleNineBoxInputSchema).output(NineBoxResultSchema),
    listCycleCalibrationReviewees: oc.route({ method: "GET", path: "/list-cycle-calibration-reviewees" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(ListCycleCalibrationRevieweesInputSchema).output(ListCycleCalibrationRevieweesOutputSchema),
    getCycleCalibrationEvidence: oc.route({ method: "GET", path: "/get-cycle-calibration-evidence" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(GetCycleCalibrationEvidenceInputSchema).output(CycleCalibrationEvidenceSchema),
    getCycleCalibrationNote: oc.route({ method: "GET", path: "/get-cycle-calibration-note" }).errors({ UPSTREAM_FAILURE: {} }).input(CycleCalibrationNoteInputSchema).output(CycleCalibrationNoteSchema),
    saveCycleCalibrationNote: oc.route({ method: "POST", path: "/save-cycle-calibration-note" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {} }).input(SaveCycleCalibrationNoteInputSchema).output(CycleCalibrationNoteSchema),
    getCycleQuestionResults: oc.route({ method: "GET", path: "/get-cycle-question-results" }).errors({ UPSTREAM_FAILURE: {}, FORBIDDEN: {}, NOT_FOUND: {} }).input(GetCycleQuestionResultsInputSchema).output(CycleQuestionResultsSchema),
    createReviewCycle: oc.route({ method: "POST", path: "/create-cycle" }).errors({ UPSTREAM_FAILURE: {} }).input(CreateReviewCycleInputSchema).output(CreateReviewCycleOutputSchema),
    patchReviewCycle: oc
        .route({ method: "POST", path: "/patch-cycle" })
        .errors({
            UPSTREAM_FAILURE: {},
            NOT_FOUND: {},
            VALIDATION_FAILED: { data: z.object({ message: z.string(), cycle: ReviewCycleDetailSchema.optional() }) }
        })
        .input(PatchReviewCycleInputSchema)
        .output(PatchReviewCycleOutputSchema),
    duplicateReviewCycle: oc.route({ method: "POST", path: "/duplicate-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleActionInputSchema).output(DuplicateReviewCycleOutputSchema),
    deleteReviewCycle: oc.route({ method: "POST", path: "/delete-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleActionInputSchema),
    lockReviewCycle: oc.route({ method: "POST", path: "/lock-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleActionInputSchema),
    unlockReviewCycle: oc.route({ method: "POST", path: "/unlock-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleActionInputSchema),
    launchReviewCycle: oc.route({ method: "POST", path: "/launch-cycle" }).errors({ UPSTREAM_FAILURE: {}, NOT_FOUND: {} }).input(CycleActionInputSchema).output(z.void())
};
